import { FiExternalLink } from 'react-icons/fi'
import { SourceSectionHeader } from './SourceBadge'

function RecordField({ label, children }) {
  if (children == null || children === '' || children === '—') return null
  return (
    <div className="rounded-xl border border-m1-line-soft bg-black/25 px-4 py-3">
      <p className="mb-1.5 text-[12px] uppercase tracking-[0.14em] text-m1-muted">{label}</p>
      <div className="text-sm text-gray-100 leading-relaxed">{children}</div>
    </div>
  )
}

function RecordTextBlock({ label, text }) {
  const trimmed = text != null ? String(text).trim() : ''
  if (!trimmed) return null
  return (
    <RecordField label={label}>
      <p className="whitespace-pre-wrap break-words">{trimmed}</p>
    </RecordField>
  )
}

function RecordLinkList({ label, links = [] }) {
  const items = (Array.isArray(links) ? links : [])
    .map((item) => {
      if (typeof item === 'string') return item.trim()
      if (item && typeof item === 'object') return String(item.url || item.href || '').trim()
      return ''
    })
    .filter((u) => /^https?:\/\//i.test(u))
  if (!items.length) return null
  return (
    <RecordField label={label}>
      <ul className="space-y-2">
        {items.map((url, i) => (
          <li key={`${url}-${i}`}>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-start gap-2 text-cyan-400 hover:text-cyan-300 break-all underline underline-offset-2 decoration-cyan-500/40 hover:decoration-cyan-300"
            >
              <FiExternalLink className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
              <span>{url}</span>
            </a>
          </li>
        ))}
      </ul>
    </RecordField>
  )
}

function formatShippingAddress(addr) {
  if (!addr || typeof addr !== 'object') return null
  const line = [
    addr.street,
    [addr.city, addr.state].filter(Boolean).join(', '),
    addr.zipCode,
    addr.country,
  ]
    .filter(Boolean)
    .join(' · ')
  return line || null
}

function formatSubGrades(record) {
  if (!record || record.gradingMode !== 'DETAILED') return null
  const parts = [
    record.gradeCentering != null && `Centering ${record.gradeCentering}`,
    record.gradeCorners != null && `Corners ${record.gradeCorners}`,
    record.gradeEdges != null && `Edges ${record.gradeEdges}`,
    record.gradeSurface != null && `Surface ${record.gradeSurface}`,
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

export function deriveRecordSections(card) {
  if (!card) return { ownerRecord: null, graderRecord: null, labRecord: null }

  const rootGraderLinks = Array.isArray(card.graderReferenceLinks) ? card.graderReferenceLinks : []
  const rootLabLinks = Array.isArray(card.labNewsLinks) ? card.labNewsLinks : []

  const mergeLinks = (fromRecord, fromRoot) => {
    const a = Array.isArray(fromRecord) ? fromRecord : []
    const b = Array.isArray(fromRoot) ? fromRoot : []
    const seen = new Set()
    const out = []
    for (const item of [...a, ...b]) {
      const u =
        typeof item === 'string'
          ? item.trim()
          : item && typeof item === 'object'
            ? String(item.url || item.href || '').trim()
            : ''
      if (!/^https?:\/\//i.test(u) || seen.has(u)) continue
      seen.add(u)
      out.push(u)
    }
    return out
  }

  if (card.ownerRecord || card.graderRecord || card.labRecord) {
    const graderRecord = card.graderRecord
      ? {
          ...card.graderRecord,
          referenceLinks: mergeLinks(card.graderRecord.referenceLinks, rootGraderLinks),
        }
      : null
    const labRecord = card.labRecord
      ? {
          ...card.labRecord,
          newsLinks: mergeLinks(card.labRecord.newsLinks, rootLabLinks),
        }
      : rootLabLinks.length
        ? {
            labNotes: null,
            authenticityStatement: null,
            ownerStoryOverride: null,
            newsLinks: rootLabLinks,
          }
        : null
    return {
      ownerRecord: card.ownerRecord ?? null,
      graderRecord,
      labRecord,
    }
  }

  const attrs = card.attributes && typeof card.attributes === 'object' ? card.attributes : {}
  const order = Array.isArray(card.orders) && card.orders.length > 0 ? card.orders[0] : null
  let shippingAddress = card.shippingAddress
  if (typeof shippingAddress === 'string') {
    try {
      shippingAddress = JSON.parse(shippingAddress)
    } catch {
      shippingAddress = null
    }
  }

  const sig =
    order?.signatureGrading && typeof order.signatureGrading === 'object'
      ? order.signatureGrading
      : {}

  return {
    ownerRecord: {
      cardTitle: order?.ownerSubmittedCardName || card.name || null,
      cardType: card.cardType || attrs.cardType || null,
      set: attrs.set ?? null,
      year: attrs.year ?? null,
      rarity: attrs.rarity ?? null,
      number: attrs.number ?? null,
      cardStory: attrs.ownerStory ?? null,
      shippingAddress,
      links: [],
    },
    graderRecord: {
      finalGrade:
        order?.gradingMode === 'RAW_ENCAPSULATION' || order?.gradingMode === 'AUTHENTIC'
          ? null
          : (order?.gradeOverall ?? order?.grade ?? null),
      gradingMode: order?.gradingMode ?? null,
      gradeCentering: order?.gradeCentering ?? null,
      gradeCorners: order?.gradeCorners ?? null,
      gradeEdges: order?.gradeEdges ?? null,
      gradeSurface: order?.gradeSurface ?? null,
      gradeCenteringNotes: order?.gradeCenteringNotes ?? null,
      gradeCornersNotes: order?.gradeCornersNotes ?? null,
      gradeEdgesNotes: order?.gradeEdgesNotes ?? null,
      gradeSurfaceNotes: order?.gradeSurfaceNotes ?? null,
      ownerSubmittedTitle: order?.ownerSubmittedCardName ?? null,
      suggestedTitle: order?.graderSuggestedCardName ?? null,
      finalTitle: order?.graderFinalCardName ?? card.name ?? null,
      conditionReport: order?.conditionReport ?? null,
      gradingNotes: order?.gradingNotes ?? null,
      certificate: order?.certificate ?? null,
      signatureGrade: sig.grade ?? null,
      signatureGradedBy: sig.gradedBy ?? null,
      referenceLinks: mergeLinks(
        attrs.graderReferenceLinks,
        rootGraderLinks.length ? rootGraderLinks : card.graderReferenceLinks
      ),
    },
    labRecord: {
      labNotes: order?.labNotes ?? null,
      authenticityStatement: order?.authenticityStatement ?? null,
      ownerStoryOverride: null,
      newsLinks: mergeLinks([], rootLabLinks),
    },
  }
}

export default function CardRecordSections({ ownerRecord, graderRecord, labRecord, card }) {
  const derived = card ? deriveRecordSections(card) : {}
  const owner = ownerRecord ?? derived.ownerRecord
  const grader = graderRecord ?? derived.graderRecord
  const lab = labRecord ?? derived.labRecord

  const ownerHas =
    owner &&
    (owner.cardTitle ||
      owner.cardType ||
      owner.set ||
      owner.year != null ||
      owner.rarity ||
      owner.number ||
      owner.cardStory ||
      owner.shippingAddress ||
      (Array.isArray(owner.links) && owner.links.length > 0))
  const graderHas =
    grader &&
    (grader.finalGrade != null ||
      grader.gradingMode ||
      grader.gradeCentering != null ||
      grader.gradeCorners != null ||
      grader.gradeEdges != null ||
      grader.gradeSurface != null ||
      grader.gradeCenteringNotes ||
      grader.gradeCornersNotes ||
      grader.gradeEdgesNotes ||
      grader.gradeSurfaceNotes ||
      grader.ownerSubmittedTitle ||
      grader.suggestedTitle ||
      grader.finalTitle ||
      grader.conditionReport ||
      grader.gradingNotes ||
      grader.certificate ||
      grader.signatureGrade != null ||
      grader.signatureGradedBy ||
      (Array.isArray(grader.referenceLinks) && grader.referenceLinks.length > 0))
  const labHas =
    lab &&
    (lab.labNotes ||
      lab.authenticityStatement ||
      lab.ownerStoryOverride ||
      (Array.isArray(lab.newsLinks) && lab.newsLinks.length > 0))

  if (!ownerHas && !graderHas && !labHas) return null

  return (
    <div className="space-y-8">
      {ownerHas && (
        <section className="m1nt-plate m1nt-plate--amber p-5 sm:p-7">
          <SourceSectionHeader
            source="owner"
            title="Owner submission"
            description="Details and media you provided when submitting this card."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RecordField label="Card title">{owner.cardTitle || '—'}</RecordField>
            <RecordField label="Card type">{owner.cardType || '—'}</RecordField>
            <RecordField label="Set / Edition">{owner.set || '—'}</RecordField>
            <RecordField label="Year">{owner.year != null ? String(owner.year) : '—'}</RecordField>
            <RecordField label="Rarity">{owner.rarity || '—'}</RecordField>
            <RecordField label="Card number">{owner.number || '—'}</RecordField>
          </div>
          <div className="mt-3 space-y-3">
            <RecordTextBlock label="Card story" text={owner.cardStory} />
            <RecordTextBlock
              label="Shipping address"
              text={formatShippingAddress(owner.shippingAddress)}
            />
            <RecordLinkList label="Links" links={owner.links} />
          </div>
        </section>
      )}

      {graderHas && (
        <section className="m1nt-plate m1nt-plate--ice p-5 sm:p-7">
          <SourceSectionHeader
            source="grader"
            title="Grader inspection"
            description="Professional grading results, notes, and reference links from your grader."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RecordField label="Final grade">
              {grader.gradingMode === 'RAW_ENCAPSULATION' ? (
                <span className="text-lg font-bold text-amber-300">Not graded</span>
              ) : grader.gradingMode === 'AUTHENTIC' ? (
                <span className="text-lg font-bold text-green-400">Authentic</span>
              ) : grader.finalGrade != null ? (
                <span className="text-lg font-bold text-emerald-300">{grader.finalGrade}</span>
              ) : (
                '—'
              )}
            </RecordField>
            <RecordField label="Grading mode">
              {grader.gradingMode === 'RAW_ENCAPSULATION'
                ? 'M1NT Raw Encapsulation (no grade)'
                : grader.gradingMode === 'AUTHENTIC'
                  ? 'Authentic (verified, no numeric grade)'
                  : grader.gradingMode === 'SIMPLE'
                    ? 'Simple (final grade only)'
                    : grader.gradingMode === 'DETAILED'
                      ? 'Detailed (sub-grades)'
                      : '—'}
            </RecordField>
            <RecordField label="Sub-grades">{formatSubGrades(grader) || '—'}</RecordField>
            <RecordField label="Owner-submitted title">
              {grader.ownerSubmittedTitle || '—'}
            </RecordField>
            <RecordField label="Grader suggested title">{grader.suggestedTitle || '—'}</RecordField>
            <RecordField label="Certificate title (final)">{grader.finalTitle || '—'}</RecordField>
          </div>
          <div className="mt-3 space-y-3">
            <RecordTextBlock label="Condition report" text={grader.conditionReport} />
            <RecordTextBlock label="Grading notes" text={grader.gradingNotes} />
            <RecordTextBlock label="Certificate notes" text={grader.certificate} />
            <RecordTextBlock label="Centering notes" text={grader.gradeCenteringNotes} />
            <RecordTextBlock label="Corners notes" text={grader.gradeCornersNotes} />
            <RecordTextBlock label="Edges notes" text={grader.gradeEdgesNotes} />
            <RecordTextBlock label="Surface notes" text={grader.gradeSurfaceNotes} />
            {(grader.signatureGrade != null || grader.signatureGradedBy) && (
              <RecordField label="Signature grading">
                <div className="space-y-1">
                  {grader.signatureGrade != null && (
                    <p>
                      Grade:{' '}
                      <span className="font-semibold text-white">{grader.signatureGrade}</span>
                    </p>
                  )}
                  {grader.signatureGradedBy && (
                    <p>
                      Authenticated by:{' '}
                      <span className="font-semibold text-white">{grader.signatureGradedBy}</span>
                    </p>
                  )}
                </div>
              </RecordField>
            )}
            <RecordLinkList label="Reference links" links={grader.referenceLinks} />
          </div>
        </section>
      )}

      {labHas && (
        <section className="m1nt-plate  p-5 sm:p-7">
          <SourceSectionHeader
            source="lab"
            title="M1NT Lab"
            description="Professional media, notes, and links added during NFT preparation."
          />
          <div className="space-y-3">
            <RecordTextBlock label="Lab notes" text={lab.labNotes} />
            <RecordTextBlock label="Authenticity statement" text={lab.authenticityStatement} />
            <RecordTextBlock label="Owner story (Lab edition)" text={lab.ownerStoryOverride} />
            <RecordLinkList label="News & reference links" links={lab.newsLinks} />
          </div>
        </section>
      )}
    </div>
  )
}

export { RecordLinkList }
